import { Component, ViewEncapsulation, computed, inject } from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';

import { NgClass } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { getIconCode, Origin8Icon } from '@origin8-web/core-ui/icon';

import { UserStore } from '../../../core/store/user/user.store';
import { AUTH_PATH } from '@origin8-web/core-ui/login/routes';
import { buildPathWithOrganization, getOrganizationFromRouter } from '../../../core/utils/organization-router.util';
import { pickSharedQueryParams } from '../../../core/utils/query-params-sync.util';
import { SupervisorsStore } from '../../../features/supervisors/store/supervisors.store';
import { SupervisorsRefreshService } from '../../../features/supervisors/store/supervisors-refresh.service';
import {
  APP_ROUTE_TITLE,
  INTEGRATIONS_ROUTE,
  INTERACTIONS_HISTORY_ROUTE,
  LEAD_LIFECYCLE_ROUTE,
  LEADS_LIBRARY_ROUTE,
  PHONE_NUMBERS_ROUTE,
  RULES_ROUTE,
  SUPERVISORS_ROUTE,
  SUPERVISOR_VIEW_ROUTE,
} from '../../../app.interface';

@Component({
  selector: 'sidebarMenu',
  standalone: true,
  templateUrl: './sidebarMenu.component.html',
  styleUrl: './sidebarMenu.component.scss',
  // Renders the Diamond layout template's own `.layout-menu` markup, so section headers, item
  // padding, indentation, hover and colours all come from `themes/diamond-layouts.scss`. Encapsulation
  // is disabled so the few extra rules in the SCSS can reach those template classes; they're scoped
  // under `.sidebar-menu` to avoid leaking app-wide.
  encapsulation: ViewEncapsulation.None,
  imports: [RouterModule, NgClass],
  providers: [SupervisorsStore],
})
export class SidebarMenu {
  readonly #userStore = inject(UserStore);
  readonly #router = inject(Router);
  readonly supervisorsStore = inject(SupervisorsStore);
  readonly #supervisorsRefresh = inject(SupervisorsRefreshService);
  readonly loggedIn = this.#userStore.loggedIn;
  readonly organization = toSignal(
    this.#router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => getOrganizationFromRouter(this.#router)),
    ),
    { initialValue: getOrganizationFromRouter(this.#router) },
  );
  readonly sharedQueryParams = toSignal(
    this.#router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => pickSharedQueryParams(this.#router.routerState.snapshot.root.queryParams)),
    ),
    { initialValue: pickSharedQueryParams(this.#router.routerState.snapshot.root.queryParams) },
  );

  /**
   * Menu model. Grouped into Supervisors / Lead lifecycle / Data / Configuration sections. Each group
   * renders as a `.layout-root-menuitem` whose label is the static (non-collapsible) section header
   * (`.layout-menuitem-root-text`) and whose `items` are the nav links. Top-level entries without
   * `items` (Login / Logout) render as standalone links. Active state comes from routerLinkActive.
   */
  readonly model = computed<MenuItem[]>(() => {
    if (!this.loggedIn()) {
      return [{ label: 'Login', icon: getIconCode(Origin8Icon.LOGIN), routerLink: '/' + AUTH_PATH.LOGIN }];
    }

    const org = this.organization();
    const queryParams = this.sharedQueryParams();
    // Exact matching keeps a parent route (e.g. lead-lifecycle) from staying active on its own
    // sub-routes (e.g. lead-lifecycle/configuration).
    const routerLinkActiveOptions = { exact: true };
    const path = (fullPath: string) => buildPathWithOrganization(fullPath, org) || '/';

    return [
      {
        // `id` lets the template render the "+" add-supervisor affordance next to this section's header.
        id: 'my-supervisors',
        label: 'Supervisors',
        items: this.#supervisorItems(org, queryParams),
      },
      {
        label: 'Lead lifecycle',
        items: [
          {
            label: 'Lead lifecycle',
            icon: getIconCode(Origin8Icon.OUTREACH),
            routerLink: path(LEAD_LIFECYCLE_ROUTE.BASE_FULL_PATH),
            queryParams,
            routerLinkActiveOptions,
          },
          {
            label: 'Lifecycle configurations',
            icon: getIconCode(Origin8Icon.SETTINGS),
            routerLink: path(LEAD_LIFECYCLE_ROUTE.CONFIGURATION_FULL_PATH),
            queryParams,
            routerLinkActiveOptions,
          },
        ],
      },
      {
        label: 'Data',
        items: [
          {
            label: 'Call History',
            icon: getIconCode(Origin8Icon.HISTORY),
            routerLink: path(INTERACTIONS_HISTORY_ROUTE.BASE_FULL_PATH),
            queryParams,
            routerLinkActiveOptions,
          },
          {
            label: APP_ROUTE_TITLE.LEADS_LIBRARY,
            icon: getIconCode(Origin8Icon.LEADS),
            routerLink: path(LEADS_LIBRARY_ROUTE.BASE_FULL_PATH),
            queryParams,
            routerLinkActiveOptions,
          },
        ],
      },
      {
        label: 'Configuration',
        items: [
          {
            label: 'Rules Library',
            icon: getIconCode(Origin8Icon.RULES),
            routerLink: path(RULES_ROUTE.BASE_FULL_PATH),
            queryParams,
            routerLinkActiveOptions,
          },
          {
            label: 'Integrations',
            icon: getIconCode(Origin8Icon.INTEGRATIONS),
            routerLink: path(INTEGRATIONS_ROUTE.BASE_FULL_PATH),
            queryParams,
            routerLinkActiveOptions,
          },
          {
            label: APP_ROUTE_TITLE.PHONE_NUMBERS,
            icon: getIconCode(Origin8Icon.PHONE_NUMBERS),
            routerLink: path(PHONE_NUMBERS_ROUTE.BASE_FULL_PATH),
            queryParams,
            routerLinkActiveOptions,
          },
        ],
      },
      // `id` lets the template pin this entry to the bottom of the sidebar.
      { id: 'logout', label: 'Logout', icon: getIconCode(Origin8Icon.LOGOUT), routerLink: '/' + AUTH_PATH.LOGOUT },
    ];
  });

  /** Org to load supervisors for — undefined unless logged in, which the rxMethod filters out. */
  readonly #supervisorsOrg = computed(() => (this.loggedIn() ? (this.organization() ?? undefined) : undefined));

  constructor() {
    // rxMethod reacts to the signal and patches state safely (no signal writes inside an effect).
    this.supervisorsStore.loadSupervisorsForOrg(this.#supervisorsOrg);

    // Reload the list when a supervisor is created/updated/deleted elsewhere (e.g. the editor page),
    // since that mutation happens against a different component-scoped SupervisorsStore instance.
    this.#supervisorsRefresh.changed$.pipe(takeUntilDestroyed()).subscribe(() => {
      const org = this.#supervisorsOrg();
      if (org) {
        this.supervisorsStore.loadSupervisorsForOrg(org);
      }
    });
  }

  /** Path to the add-supervisor page for the current org — used by the "+" affordance in the section header. */
  addSupervisorPath(): string {
    return buildPathWithOrganization(SUPERVISORS_ROUTE.ADD_FULL_PATH, this.organization()) || '/';
  }

  /** Children of the "Supervisors" section: loading/empty placeholder, then each supervisor. */
  #supervisorItems(org: string | null | undefined, queryParams: Record<string, unknown>): MenuItem[] {
    if (this.supervisorsStore.isLoading()) {
      return [{ label: 'Loading…', disabled: true }];
    }

    const supervisors = this.supervisorsStore.supervisors();
    if (supervisors.length === 0) {
      return [{ label: 'No supervisors yet', disabled: true }];
    }

    const supervisorItems = supervisors.map<MenuItem>((supervisor) => ({
      label: supervisor.supervisorName,
      // A filled dot coloured by the styleClass (active = emerald, inactive = zinc) via SCSS.
      icon: 'pi pi-circle-fill',
      styleClass: supervisor.isActive ? 'supervisor-active' : 'supervisor-inactive',
      routerLink:
        buildPathWithOrganization(SUPERVISOR_VIEW_ROUTE.BASE_FULL_PATH, org, {
          supervisorId: supervisor.id,
        }) || '/',
      queryParams,
      routerLinkActiveOptions: { exact: false },
    }));

    return supervisorItems;
  }
}
